/**
=========================================================
* Material Dashboard 3 PRO React - v2.3.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// @mui material components
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 3 PRO React examples
import PageLayout from "examples/LayoutContainers/PageLayout";

// Material Dashboard 3 PRO React context
import { useMaterialUIController, setLayout } from "context";

// Context
import { useAuthContext } from "contextAuth";
import { useBranding, FRESENIUS } from "contextBranding";
import colors from "assets/theme/base/colors";

function Landing() {
  const { t, i18n } = useTranslation();
  const { userInfo, logoutUser, updateUserInfo, checkRefreshUser, isInitialized } =
    useAuthContext();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [controller, dispatch] = useMaterialUIController();
  const { sbiDashboardTheme } = controller;
  const { isFreseniusBranding, setFreseniusBranding, appendBrandingSearch } = useBranding();
  const isLight = sbiDashboardTheme === "light";
  const modalPalette = isFreseniusBranding
    ? (isLight ? FRESENIUS.themeLight : FRESENIUS.themeDark)
    : (isLight ? colors.sbiDashboardTheme.light : colors.sbiDashboardTheme.dark);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);
  const [editAccountModalOpen, setEditAccountModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
  });

  //   useEffect(() => {
  //     setLayout(dispatch, "page");
  //   }, [pathname, dispatch]);

  //   useEffect(() => {
  //     const checkAuth = async () => {
  //       if (isInitialized) {
  //         const isAuthenticated = await checkRefreshUser();
  //         if (!isAuthenticated) {
  //           navigate("/sign-in");
  //         }
  //       }
  //     };
  //     checkAuth();
  //   }, [isInitialized, checkRefreshUser, navigate]);

  // Get user's full name
  const getUserName = () => {
    if (userInfo) {
      const firstName = userInfo.first_name || "";
      const lastName = userInfo.last_name || "";
      return `${firstName} ${lastName}`.trim() || "User";
    }
    return "User";
  };

  const handleLogout = async () => {
    await logoutUser();
  };

  const handleLanguageButtonClick = (event) => {
    setLanguageMenuAnchor(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchor(null);
  };

  const handleLanguageSelect = (lng) => {
    if (!lng) return;
    i18n.changeLanguage(lng);
    setLanguageMenuAnchor(null);
  };

  const handleEditAccountOpen = () => {
    if (userInfo) {
      setEditForm({
        first_name: userInfo.first_name || "",
        last_name: userInfo.last_name || "",
        email: userInfo.email || "",
      });
    }
    setEditAccountModalOpen(true);
  };

  const handleEditAccountClose = () => {
    setEditAccountModalOpen(false);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditAccountSave = () => {
    updateUserInfo({
      ...(userInfo || {}),
      first_name: editForm.first_name,
      last_name: editForm.last_name,
      email: editForm.email,
    });
    setEditAccountModalOpen(false);
  };

  return (
    <PageLayout background="default">
      <MDBox
        position="absolute"
        width="100%"
        minHeight="100vh"
        sx={(theme) => ({
          backgroundColor: isFreseniusBranding
            ? FRESENIUS.blueDark
            : theme.palette.backgroundSbi.secondary,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        })}
      />
      <MDBox
        px={1}
        width="100%"
        height="100vh"
        mx="auto"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        position="relative"
        zIndex={1}
      >
        <MDBox width="100%" maxWidth="1000px" px={3}>
          {/* Logo Section - Outside Card */}
          <MDBox textAlign="center" mb={6}>
            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={3}
              flexWrap="wrap"
            >
              {isFreseniusBranding && (
                <img
                  src={`${process.env.PUBLIC_URL}/${isLight ? "fresenius_logo.png" : "fresenius_logo_white.png"}`}
                  alt="Fresenius Kabi"
                  style={{ height: "56px", width: "auto" }}
                />
              )}
              <img
                src={`${process.env.PUBLIC_URL}/logo_with_text_light.svg`}
                alt="Safe Brain Initiative"
                style={{
                  height: isFreseniusBranding ? "40px" : "60px",
                  width: "auto",
                  opacity: isFreseniusBranding ? 0.9 : 1,
                }}
              />
            </MDBox>
          </MDBox>

          {/* Main Card */}
          <Card
            sx={(theme) => ({
              backgroundColor: isFreseniusBranding
                ? `${FRESENIUS.blue}ee`
                : `${theme.palette.backgroundSbi.default}99`,
              backdropFilter: "blur(10px)",
              border: isFreseniusBranding
                ? `2px solid ${FRESENIUS.borderStrong}`
                : `2px solid ${theme.palette.backgroundSbi.default}80`,
              boxShadow: isFreseniusBranding
                ? "0 8px 32px rgba(0, 0, 0, 0.2)"
                : "none",
              borderRadius: "20px",
              padding: { xs: "2rem", md: "3rem" },
              mb: 3,
            })}
          >
            {/* Top Section with Person Icon and Greeting */}
            <Grid container spacing={1} alignItems="flex-start" mb={4}>
              {/* Person Icon on Left */}
              <Grid
                item
                xs={12}
                md={2}
                display="flex"
                justifyContent={{ xs: "center", md: "flex-start" }}
              >
                <MDBox
                  sx={(theme) => ({
                    width: "120px",
                    height: "120px",
                    borderRadius: "50%",
                    backgroundColor: isFreseniusBranding
                      ? `${FRESENIUS.blueLight}99`
                      : `${theme.palette.backgroundSbi.secondary}80`,
                    border: isFreseniusBranding
                      ? `2px solid ${FRESENIUS.border}`
                      : `2px solid ${theme.palette.backgroundSbi.default}80`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  })}
                >
                  <Icon
                    sx={{
                      fontSize: "5rem !important",
                      color: "rgba(255, 255, 255, 0.7) !important",
                    }}
                  >
                    account_circle
                  </Icon>
                </MDBox>
              </Grid>

              {/* Greeting Text on Right */}
              <Grid item xs={12} md={10}>
                <MDTypography
                  variant="h2"
                  fontWeight="bold"
                  color="white"
                  mb={0.5}
                  sx={(theme) => ({
                    background: isFreseniusBranding
                      ? `linear-gradient(135deg, ${FRESENIUS.white} 0%, ${FRESENIUS.blueLight} 100%)`
                      : `linear-gradient(135deg, ${theme.palette.accent.tealLight} 0%, ${theme.palette.accent.cyan} 100%)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  })}
                >
                  {t("landing.greetingHello")} {getUserName()}
                </MDTypography>
                <MDTypography
                  variant="h5"
                  fontWeight="regular"
                  color="white"
                  mb={2}
                >
                  {t("landing.greetingSubtitle")}
                </MDTypography>
                <MDTypography
                  variant="body1"
                  color="white"
                  mb={0}
                  sx={{ opacity: 0.7 }}
                >
                  {t("landing.chooseAccess")}
                </MDTypography>
              </Grid>
            </Grid>

            {/* Update Message - Centered */}
            <MDBox textAlign="center" mb={4}>
              <MDTypography variant="body2" color="white" sx={{ opacity: 0.7 }}>
                {t("landing.updateMessage")}{" "}
                <MDBox
                  component={Link}
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    // Add navigation to update info page if needed
                  }}
                  sx={(theme) => ({
                    color: isFreseniusBranding
                      ? FRESENIUS.white
                      : theme.palette.backgroundSbi.secondary,
                    textDecoration: "underline",
                    display: "inline",
                  })}
                >
                  {t("landing.updateMessageLink")}
                </MDBox>
                .
              </MDTypography>
            </MDBox>

            {/* Action Buttons - Square Cards */}
            <Grid container spacing={2} justifyContent="center">
              {/* Dashboard Button - Coral/Orange */}
              <Grid item xs={6} sm={3}>
                <MDBox
                  onClick={() => navigate(appendBrandingSearch("/sbidark"))}
                  sx={(theme) => ({
                    backgroundColor: isFreseniusBranding
                      ? FRESENIUS.blue
                      : theme.palette.primary.main,
                    border: isFreseniusBranding
                      ? `2px solid ${FRESENIUS.borderStrong}`
                      : "none",
                    borderRadius: "16px",
                    padding: "1.5rem 1rem",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "transform 0.2s, opacity 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      opacity: 0.9,
                    },
                    minHeight: "140px",
                  })}
                >
                  <Icon
                    sx={(theme) => ({
                      fontSize: "3rem !important",
                      color: `${
                        isFreseniusBranding
                          ? FRESENIUS.white
                          : theme.palette.accent.tealLight
                      } !important`,
                      mb: 1,
                    })}
                  >
                    dashboard
                  </Icon>
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    color="white"
                    textAlign="center"
                  >
                    {t("landing.dashboardLabel")}
                  </MDTypography>
                </MDBox>
              </Grid>

              {/* Digital Assessment Button */}
              <Grid item xs={6} sm={3}>
                <MDBox
                  onClick={() => navigate(appendBrandingSearch("/digital-assessment"))}
                  sx={(theme) => ({
                    backgroundColor: isFreseniusBranding
                      ? FRESENIUS.blueLight
                      : theme.palette.backgroundSbi.secondary,
                    border: isFreseniusBranding
                      ? `2px solid ${FRESENIUS.border}`
                      : "none",
                    borderRadius: "16px",
                    padding: "1.5rem 1rem",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "transform 0.2s, opacity 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      opacity: 0.9,
                    },
                    minHeight: "140px",
                  })}
                >
                  <Icon
                    sx={(theme) => ({
                      fontSize: "3rem !important",
                      color: `${
                        isFreseniusBranding
                          ? FRESENIUS.white
                          : theme.palette.accent.tealLight
                      } !important`,
                      mb: 1,
                    })}
                  >
                    assignment
                  </Icon>
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    color="white"
                    textAlign="center"
                  >
                    {t("landing.digitalAssessmentLabel")}
                  </MDTypography>
                </MDBox>
              </Grid>

              {/* E-Learning Button */}
              <Grid item xs={6} sm={3}>
                <MDBox
                  onClick={() => navigate(appendBrandingSearch("/e-learning"))}
                  sx={(theme) => ({
                    backgroundColor: isFreseniusBranding
                      ? FRESENIUS.blueLight
                      : theme.palette.backgroundSbi.secondary,
                    border: isFreseniusBranding
                      ? `2px solid ${FRESENIUS.border}`
                      : "none",
                    borderRadius: "16px",
                    padding: "1.5rem 1rem",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "transform 0.2s, opacity 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      opacity: 0.9,
                    },
                    minHeight: "140px",
                  })}
                >
                  <Icon
                    sx={(theme) => ({
                      fontSize: "3rem !important",
                      color: `${
                        isFreseniusBranding
                          ? FRESENIUS.white
                          : theme.palette.accent.tealLight
                      } !important`,
                      mb: 1,
                    })}
                  >
                    school
                  </Icon>
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    color="white"
                    textAlign="center"
                  >
                    {t("landing.eLearningLabel")}
                  </MDTypography>
                </MDBox>
              </Grid>

              {/* Connect Button */}
              <Grid item xs={6} sm={3}>
                <MDBox
                  onClick={() => navigate(appendBrandingSearch("/device-selection"))}
                  sx={(theme) => ({
                    backgroundColor: isFreseniusBranding
                      ? FRESENIUS.blueLight
                      : theme.palette.backgroundSbi.secondary,
                    border: isFreseniusBranding
                      ? `2px solid ${FRESENIUS.border}`
                      : "none",
                    borderRadius: "16px",
                    padding: "1.5rem 1rem",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "transform 0.2s, opacity 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      opacity: 0.9,
                    },
                    minHeight: "140px",
                  })}
                >
                  <Icon
                    sx={(theme) => ({
                      fontSize: "3rem !important",
                      color: `${
                        isFreseniusBranding
                          ? FRESENIUS.white
                          : theme.palette.accent.tealLight
                      } !important`,
                      mb: 1,
                    })}
                  >
                    cable
                  </Icon>
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    color="white"
                    textAlign="center"
                  >
                    {t("landing.connectLabel")}
                  </MDTypography>
                </MDBox>
              </Grid>
            </Grid>
          </Card>

          {/* Bottom Links */}
          <MDBox
            display="flex"
            justifyContent="center"
            gap={4}
            flexWrap="wrap"
            sx={{ mt: 2 }}
          >
            <MDBox
              component={Link}
              to="#"
              onClick={(e) => {
                e.preventDefault();
                handleEditAccountOpen();
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "white",
                textDecoration: "none",
                cursor: "pointer",
                "&:hover": {
                  opacity: 0.8,
                },
              }}
            >
              <MDTypography variant="h6" color="white" sx={{ opacity: 0.7 }}>
                {t("landing.editAccount")}
              </MDTypography>
              <Icon
                sx={{
                  fontSize: "1.5rem !important",
                  color: "white !important",
                  opacity: 0.7,
                }}
              >
                edit
              </Icon>
            </MDBox>
            <MDBox
              component="div"
              onClick={handleLogout}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "white",
                cursor: "pointer",
                "&:hover": {
                  opacity: 0.8,
                },
              }}
            >
              <MDTypography variant="h6" color="white" sx={{ opacity: 0.7 }}>
                {t("landing.logout")}
              </MDTypography>
              <Icon
                sx={{
                  fontSize: "1.5rem !important",
                  color: "white !important",
                  opacity: 0.7,
                }}
              >
                logout
              </Icon>
            </MDBox>
            <MDBox
              component="div"
              onClick={handleLanguageButtonClick}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "white",
                cursor: "pointer",
                "&:hover": {
                  opacity: 0.8,
                },
              }}
            >
              <MDTypography variant="h6" color="white" sx={{ opacity: 0.7 }}>
                {t("landing.languageName")}
              </MDTypography>
              <Icon
                sx={{
                  fontSize: "1.5rem !important",
                  color: "white !important",
                  opacity: 0.7,
                }}
              >
                language
              </Icon>
            </MDBox>
            <MDBox
              component="div"
              onClick={() => setFreseniusBranding(!isFreseniusBranding)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "white",
                cursor: "pointer",
                "&:hover": {
                  opacity: 0.8,
                },
              }}
            >
              <MDTypography variant="h6" color="white" sx={{ opacity: 0.7 }}>
                {isFreseniusBranding
                  ? t("landing.brandingSbi")
                  : t("landing.brandingFresenius")}
              </MDTypography>
              <Icon
                sx={{
                  fontSize: "1.5rem !important",
                  color: "white !important",
                  opacity: 0.7,
                }}
              >
                palette
              </Icon>
            </MDBox>
            <Menu
              anchorEl={languageMenuAnchor}
              open={Boolean(languageMenuAnchor)}
              onClose={handleLanguageMenuClose}
              anchorOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
            >
              <MenuItem onClick={() => handleLanguageSelect("en")}>
                English
              </MenuItem>
              <MenuItem onClick={() => handleLanguageSelect("de")}>
                Deutsch
              </MenuItem>
              <MenuItem onClick={() => handleLanguageSelect("fr")}>
                Français
              </MenuItem>
              <MenuItem onClick={() => handleLanguageSelect("es")}>
                Español
              </MenuItem>
            </Menu>

          {/* Edit Account Modal – uses light/dark theme and branding colors */}
          <Dialog
            open={editAccountModalOpen}
            onClose={handleEditAccountClose}
            PaperProps={{
              sx: {
                backgroundColor: modalPalette.bgMain,
                backdropFilter: "blur(12px)",
                border: `1px solid ${modalPalette.border}`,
                borderRadius: 2,
                minWidth: 400,
                maxWidth: 480,
              },
            }}
          >
            <DialogTitle
              sx={{
                color: modalPalette.textPrimary,
                fontWeight: 600,
              }}
            >
              {t("landing.editAccountModalTitle")}
            </DialogTitle>
            <DialogContent>
              <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
                <TextField
                  label={t("landing.firstName")}
                  value={editForm.first_name}
                  onChange={(e) => handleEditFormChange("first_name", e.target.value)}
                  fullWidth
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: modalPalette.textPrimary,
                      "& fieldset": {
                        borderColor: modalPalette.border,
                      },
                      "&:hover fieldset": {
                        borderColor: modalPalette.link,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: modalPalette.textSecondary || modalPalette.textPrimary,
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: modalPalette.link,
                    },
                  }}
                />
                <TextField
                  label={t("landing.lastName")}
                  value={editForm.last_name}
                  onChange={(e) => handleEditFormChange("last_name", e.target.value)}
                  fullWidth
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: modalPalette.textPrimary,
                      "& fieldset": {
                        borderColor: modalPalette.border,
                      },
                      "&:hover fieldset": {
                        borderColor: modalPalette.link,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: modalPalette.textSecondary || modalPalette.textPrimary,
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: modalPalette.link,
                    },
                  }}
                />
                <TextField
                  label={t("landing.email")}
                  type="email"
                  value={editForm.email}
                  onChange={(e) => handleEditFormChange("email", e.target.value)}
                  fullWidth
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: modalPalette.textPrimary,
                      "& fieldset": {
                        borderColor: modalPalette.border,
                      },
                      "&:hover fieldset": {
                        borderColor: modalPalette.link,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: modalPalette.textSecondary || modalPalette.textPrimary,
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: modalPalette.link,
                    },
                  }}
                />
              </MDBox>
            </DialogContent>
            <DialogActions sx={{ p: 2, pt: 0 }}>
              <MDButton
                variant="outlined"
                onClick={handleEditAccountClose}
                sx={{
                  fontWeight: 600,
                  borderColor: modalPalette.border,
                  color: modalPalette.textPrimary,
                  "&:hover": {
                    borderColor: modalPalette.link,
                    backgroundColor: isLight ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.06)",
                  },
                }}
              >
                {t("landing.cancel")}
              </MDButton>
              <MDButton
                variant="contained"
                onClick={handleEditAccountSave}
                sx={{
                  fontWeight: 600,
                  backgroundColor: isFreseniusBranding ? FRESENIUS.blue : colors.primarySbi?.main,
                  color: isFreseniusBranding ? FRESENIUS.white : colors.textSbi?.onPrimary,
                  "&:hover": {
                    backgroundColor: isFreseniusBranding ? FRESENIUS.blueLight : colors.primarySbi?.focus,
                  },
                }}
              >
                {t("landing.save")}
              </MDButton>
            </DialogActions>
          </Dialog>
          </MDBox>
        </MDBox>
      </MDBox>
    </PageLayout>
  );
}

export default Landing;
